import { i18n } from "@/lib/i18n";
import type { nl } from "@/lib/i18n/locales/nl";

/**
 * Where "Sign in with Microsoft" goes. A full-page navigation, never
 * a fetch through the SDK: the backend answers with a chain of redirects
 * through Supabase and Microsoft, and ends on `/home` with a session or on
 * `/login?error=<code>` without one. `/api` is the same-origin proxy prefix.
 */
export const MICROSOFT_SIGN_IN_PATH = "/api/auth/microsoft/start";

/** A Supabase PKCE auth code: a UUID. */
const SUPABASE_AUTH_CODE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** A code the login page explains: the ones the Microsoft callback sends back. */
type SignInErrorCode = keyof typeof nl.login.microsoftErrors;

const SIGN_IN_ERROR_CODES: ReadonlySet<string> = new Set<SignInErrorCode>([
  "microsoft_cancelled",
  "microsoft_failed",
  "email_not_verified",
  "domain_not_allowed",
  "no_account",
]);

function isSignInErrorCode(code: string): code is SignInErrorCode {
  return SIGN_IN_ERROR_CODES.has(code);
}

/**
 * The message, in the dashboard's language, for the `?error=<code>` the
 * Microsoft sign-in callback (or the root page) sends back to the login page;
 * null without a code. A code this page does not know reads as a failed
 * sign-in, and is never echoed.
 */
export function microsoftSignInError(code: string | null): string | null {
  if (!code) {
    return null;
  }
  return i18n.t(`login.microsoftErrors.${isSignInErrorCode(code) ? code : "microsoft_failed"}`);
}

/**
 * Where `/` sends the browser. Supabase falls back to the site root instead of
 * the callback when it cannot use the callback (an expired sign-in state, or a
 * callback URL missing from its allow list), with `error` or `code` in the
 * query or the fragment. Such a visit ends on the login page with the failed
 * message instead of a silent detour through `/home`. Supabase's auth codes
 * are UUIDs, so an unrelated `?code=` (a promo code, say) still goes to `/home`.
 */
export function rootRedirectTarget(search: string, hash: string): string {
  const parts = [search.replace(/^\?/, ""), hash.replace(/^#/, "")].map(
    (part) => new URLSearchParams(part),
  );
  const fromSignIn = parts.some(
    (params) => params.has("error") || SUPABASE_AUTH_CODE.test(params.get("code") ?? ""),
  );
  return fromSignIn ? "/login?error=microsoft_failed" : "/home";
}
